import styled from 'styled-components'
import CustomerUserCard from '../components/CustomerUserCard'
import { useMemo, useState } from 'react'
import { fetcher } from '../utils/fetcher'
import { Title } from 'react-head'
import { Button } from '@bm-js/atlascloud-ui'
import Loader from '../components/Loader'
import { breakPointsCss, colors } from '../utils/config'
import CustomerUserForm, {
  CustomerUserInput,
} from '../components/CustomerUserForm'
import DeleteConfirmation from '../components/DeleteConfirmation'
import { CustomerUserBM } from '@bm-js/atlascloud-shared'
import { usePopup, useToast, useToken } from '../utils/hooks'
import { useApi } from '../utils/useApi'
import { useTr } from '../utils/useTr'
import { TrKey } from '../cnst/translations/trKey.cnst'

const StyledCustomerUsers = styled.div`
  .loader-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
  }
  ${breakPointsCss.tablet} {
    .headers {
      display: block;
    }
  }
`

const CustomerUsers = () => {
  const toast = useToast()
  const token = useToken()
  const { tr } = useTr()
  const { closePopup, setPopup } = usePopup()

  const { data: users = [], refetch } =
    useApi<CustomerUserBM[]>('customer-users')
  const [loading, setLoading] = useState(false)

  const deleteUserInit = (user: CustomerUserBM) => {
    setPopup(
      <DeleteConfirmation
        item={user}
        deleteKey={'fullName'}
        name={tr(TrKey.customerUsersTheUser)}
        handleCancel={closePopup}
        handleDelete={deleteUser}
      />
    )
  }

  const deleteUser = async (user: CustomerUserBM, cb: () => void) => {
    setLoading(true)
    const { err } = await fetcher({
      path: `customer-users/${user._id}`,
      method: 'DELETE',
      auth: token,
    })
    if (err) {
      cb()
      toast.error(tr(TrKey.customerUsersFailedToDelete))
      setLoading(false)
      return
    }
    cb()
    closePopup()
    toast.success(tr(TrKey.customerUsersUserDeleted))
    await refetch()
    setLoading(false)
  }

  const createUserInit = () => {
    setPopup(
      <CustomerUserForm handleSubmit={createUser} closePopup={closePopup} />
    )
  }

  const createUser = async (
    formData: CustomerUserInput,
    cb: () => void,
    errCb: () => void
  ) => {
    const { err } = await fetcher({
      path: 'customer-users',
      method: 'POST',
      body: formData,
      auth: token,
    })
    if (err) {
      toast.error(tr(TrKey.customerUsersFailedToCreate))
      errCb()
      return
    }
    toast.success(tr(TrKey.customerUsersUserCreated))
    cb()
    await refetch()
    setLoading(false)
    closePopup()
  }

  const editUserInit = (user: CustomerUserBM) => {
    setPopup(
      <CustomerUserForm
        handleSubmit={editUser}
        closePopup={closePopup}
        data={user}
      />
    )
  }
  const editUser = async (
    formData: CustomerUserInput,
    cb: () => void,
    errCb: () => void
  ) => {
    const { err } = await fetcher({
      path: `customer-users/${formData._id}`,
      method: 'POST',
      body: formData,
      auth: token,
    })
    if (err) {
      toast.error(tr(TrKey.customerUsersFailedToUpdate))
      errCb()
      return
    }
    toast.success(tr(TrKey.customerUsersUserUpdated))
    cb()
    await refetch()
    setLoading(false)
    closePopup()
  }

  const title = useMemo(() => {
    return tr(TrKey.customerUsersRouteTitle) + ' | Atlascloud'
  }, [tr])

  if (loading)
    return (
      <StyledCustomerUsers>
        <div className='loader-container'>
          <Loader color={colors.primary400} />
        </div>
      </StyledCustomerUsers>
    )
  return (
    <StyledCustomerUsers className='content'>
      <Title>{title}</Title>
      <div className='headers jcsb ac mbottom1 flex'>
        <h1 className='m0 l primblue'>{tr(TrKey.customerUsersRouteTitle)}</h1>

        <Button onClick={createUserInit}>
          {tr(TrKey.customerUsersCreateUser)}
        </Button>
      </div>
      <div className='users'>
        {users &&
          users.map((user) => (
            <CustomerUserCard
              deleteUser={() => deleteUserInit(user)}
              editUserInit={() => editUserInit(user)}
              user={user}
              key={user._id}
            />
          ))}
      </div>
    </StyledCustomerUsers>
  )
}

export default CustomerUsers
